import React, { useMemo, useState } from 'react';
import './V1MarketPlusRsPanel.css';

const PANEL_MIN = 10;
const PANEL_MAX = 1000;
const RECENT_MS = 20 * 60 * 1000;

function fmtRs(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toFixed(2)}`;
}

function fmtMove(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}₹${v.toFixed(2)}`;
}

function inPanelBand(price, meta) {
  const p = Number(price);
  const min = Number(meta?.priceBandMin) > 0 ? Number(meta.priceBandMin) : PANEL_MIN;
  const max = Number(meta?.priceBandMax) > 0 ? Number(meta.priceBandMax) : PANEL_MAX;
  return p >= min && p <= max;
}

function isRecentHit(row, meta) {
  if (row.recentHit === true) return true;
  const threshold = Number(meta?.thresholdRs) > 0
    ? Number(meta.thresholdRs)
    : (Number(row?.thresholdRs) > 0 ? Number(row.thresholdRs) : 1);
  const ltp = Number(row.ltpMoveRs);
  if (!(ltp + 0.004 >= threshold)) return false;
  const recentMs = Number(meta?.recentHitMs) > 0 ? Number(meta.recentHitMs) : RECENT_MS;
  const now = Date.now();
  const first = Number(row.firstHitAt) || 0;
  const ltpAt = Number(row.ltpHitAt) || first;
  return (first > 0 && now - first <= recentMs)
    || (ltpAt > 0 && now - ltpAt <= recentMs);
}

/**
 * Market-wide sticky list: names that reached Master +₹N/sh from prev close today.
 * Panel shows ₹10–₹1000 LTP · UP only · blinks fresh +₹/sh hits.
 */
export default function V1MarketPlusRsPanel({ rows = [], meta = null, priceMap = null, liveStatus = null }) {
  const [open, setOpen] = useState(true);
  const threshold = Number(meta?.thresholdRs) > 0
    ? Number(meta.thresholdRs)
    : (Number(rows?.[0]?.thresholdRs) > 0 ? Number(rows[0].thresholdRs) : 1);
  const bandMin = Number(meta?.priceBandMin) > 0 ? Number(meta.priceBandMin) : PANEL_MIN;
  const bandMax = Number(meta?.priceBandMax) > 0 ? Number(meta.priceBandMax) : PANEL_MAX;

  const list = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const pm = priceMap && typeof priceMap === 'object' ? priceMap : {};
    return [...rows]
      .map((row) => {
        const sk = String(row.stock || row.symbol || '').replace(/\.(NS|BO)$/i, '');
        const q = pm[sk] || pm[row.stock];
        if (!q?.price) return row;
        const price = Number(q.price);
        const prevClose = Number(row.prevClose) || 0;
        const ltpMoveRs = prevClose > 0
          ? Math.round((price - prevClose) * 100) / 100
          : row.ltpMoveRs;
        const changePct = prevClose > 0
          ? Math.round(((price - prevClose) / prevClose) * 10000) / 100
          : row.changePct;
        return {
          ...row,
          price,
          ltpMoveRs,
          changePct,
          livePriceAt: q.fetchedAt || Date.now()
        };
      })
      .filter((r) => inPanelBand(r.price, meta))
      .filter((r) => (Number(r.ltpMoveRs) || 0) > 0)
      .sort((a, b) => {
        const ra = isRecentHit(a, meta) ? 1 : 0;
        const rb = isRecentHit(b, meta) ? 1 : 0;
        if (rb !== ra) return rb - ra;
        return (Number(b.peakMoveRs || b.moveRs) - Number(a.peakMoveRs || a.moveRs))
          || (Number(b.ltpMoveRs) - Number(a.ltpMoveRs));
      })
      .map((r, i) => ({ ...r, rank: i + 1, recentHit: isRecentHit(r, meta) }));
  }, [rows, meta, priceMap]);

  const recentCount = meta?.recentHitCount != null
    ? meta.recentHitCount
    : list.filter((r) => r.recentHit).length;
  const funnel = meta?.filterFunnel || {};
  const scanned = meta?.quotesScanned;
  const universe = meta?.universeSize;
  const [showFilters, setShowFilters] = useState(true);

  return (
    <section className="v1-plus-rs card" aria-label={`Market +₹${threshold}/sh today`}>
      <header className="v1-plus-rs-head">
        <button
          type="button"
          className="v1-plus-rs-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <h3>
            +₹{threshold.toFixed(threshold % 1 ? 2 : 0)}/SH TODAY
            <span className="v1-plus-rs-badge">₹{bandMin}–₹{bandMax} UP</span>
          </h3>
          <span className="v1-plus-rs-meta">
            {list.length} up
            {recentCount > 0 ? ` · ${recentCount} blinking` : ''}
            {universe != null ? ` · scan ${universe}` : ''}
            {scanned != null ? ` · quoted ${scanned}` : ''}
            {liveStatus === 'live' ? ' · LTP live' : (liveStatus === 'reconnecting' ? ' · LTP reconnecting' : '')}
            <span className="v1-plus-rs-chevron" aria-hidden>{open ? '▾' : '▸'}</span>
          </span>
        </button>
        <p className="v1-plus-rs-sub">
          LTP ₹{bandMin}–₹{bandMax} · still green today · blinking = hit +₹{threshold % 1 ? threshold.toFixed(2) : threshold}/sh
          in the last 20 min (fresh profit move).
        </p>
        <div className="v1-plus-rs-funnel-wrap">
          <button
            type="button"
            className="v1-plus-rs-funnel-toggle"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
          >
            {showFilters ? 'Hide filters' : 'Show filters'}
          </button>
          {showFilters && (
            <div className="v1-plus-rs-funnel" aria-label="Panel filter funnel">
              <div className="v1-plus-rs-funnel-step">
                <span className="v1-plus-rs-funnel-num">{funnel.universe ?? universe ?? '—'}</span>
                <span className="v1-plus-rs-funnel-lbl">Universe scanned</span>
              </div>
              <span className="v1-plus-rs-funnel-arrow">→</span>
              <div className="v1-plus-rs-funnel-step">
                <span className="v1-plus-rs-funnel-num">{funnel.totalHits ?? meta?.totalHits ?? '—'}</span>
                <span className="v1-plus-rs-funnel-lbl">Hit +₹{threshold}/sh today</span>
              </div>
              <span className="v1-plus-rs-funnel-arrow">→</span>
              <div className="v1-plus-rs-funnel-step highlight">
                <span className="v1-plus-rs-funnel-num">{funnel.inBand ?? '—'}</span>
                <span className="v1-plus-rs-funnel-lbl">LTP ₹{bandMin}–{bandMax}</span>
                {(funnel.excludedPrice > 0) && (
                  <span className="v1-plus-rs-funnel-drop">−{funnel.excludedPrice} price</span>
                )}
              </div>
              <span className="v1-plus-rs-funnel-arrow">→</span>
              <div className="v1-plus-rs-funnel-step highlight">
                <span className="v1-plus-rs-funnel-num">{funnel.upToday ?? list.length}</span>
                <span className="v1-plus-rs-funnel-lbl">Still UP (LTP green)</span>
                {(funnel.fadedInBand > 0) && (
                  <span className="v1-plus-rs-funnel-drop">−{funnel.fadedInBand} faded</span>
                )}
              </div>
              <span className="v1-plus-rs-funnel-arrow">→</span>
              <div className="v1-plus-rs-funnel-step blink-step">
                <span className="v1-plus-rs-funnel-num">{funnel.recentBlink ?? recentCount}</span>
                <span className="v1-plus-rs-funnel-lbl">Blinking (20 min)</span>
              </div>
            </div>
          )}
        </div>
      </header>
      {open && (
        list.length === 0 ? (
          <p className="v1-plus-rs-empty">
            No UP names in ₹{bandMin}–₹{bandMax} at +₹{threshold % 1 ? threshold.toFixed(2) : threshold}/sh yet.
            Scanning NSE/BSE in the background…
          </p>
        ) : (
          <div className="v1-plus-rs-table-wrap">
            <table className="v1-plus-rs-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Stock</th>
                  <th>Exch</th>
                  <th>LTP</th>
                  <th>Day high</th>
                  <th>Peak +₹/sh</th>
                  <th>LTP +₹/sh</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr
                    key={row.symbol || row.stock}
                    className={row.recentHit ? 'v1-plus-rs-blink' : undefined}
                  >
                    <td>{row.rank ?? '—'}</td>
                    <td className="v1-plus-rs-stock">
                      {row.stock || row.symbol}
                      {row.recentHit ? <span className="v1-plus-rs-fresh"> NEW</span> : null}
                    </td>
                    <td>{row.exchange || '—'}</td>
                    <td>{fmtRs(row.price)}</td>
                    <td>{fmtRs(row.dayHigh)}</td>
                    <td className="up">{fmtMove(row.peakMoveRs ?? row.moveRs)}</td>
                    <td className={(Number(row.ltpMoveRs) || 0) >= 0 ? 'up' : 'down'}>
                      {fmtMove(row.ltpMoveRs)}
                    </td>
                    <td className={(Number(row.changePct) || 0) >= 0 ? 'up' : 'down'}>
                      {Number.isFinite(Number(row.changePct))
                        ? `${Number(row.changePct) >= 0 ? '+' : ''}${Number(row.changePct).toFixed(2)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}
